import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionButton } from '@/components/ui/ActionButton';
import { useToast } from '@/components/ui/Toast';
import { apiClient } from '@/lib/api';
import type { ApprovalAction, ApprovalRequest } from '@/lib/types';
import type { APPayload, APSummary } from './types';
import { ReprocessForm, type ReprocessFormHandle, type APReprocessEdits } from './ReprocessForm';
import { ServiceInvoiceModal } from './ServiceInvoiceModal';
import { DrivePreview } from './DrivePreview';
import {
  approveDisabledReason,
  servicePostDisabledReason,
  shouldShowApprove,
  shouldShowPostAsService,
} from './actionVisibility';
import { BucketView } from './buckets';
import { deriveIdempotencyKey } from './idempotency';
import type { SupplierHit } from './SupplierSearchPicker';
import type { SupplierCandidate } from './types';
import { APActivityComments } from './APActivityComments';

const SLUG = 'ap-purchase-invoice';
const QK_QUEUE = ['queue', SLUG];
const QK_HISTORY = ['history', SLUG];
const QK_STATS = ['stats', SLUG];

type APRequest = ApprovalRequest<APPayload, APSummary> & { actions?: ApprovalAction[] };

type Lifecycle =
  | 'needs_correction'
  | 'pending_approval'
  | 'posted'
  | 'rejected'
  | 'service_blocked';

function deriveLifecycle(req: APRequest): Lifecycle {
  if (req.status === 'posted') return 'posted';
  if (req.status === 'rejected') return 'rejected';
  const lc = (req.summary as APSummary | undefined)?.['lifecycle'];
  if (typeof lc === 'string' && lc) return lc as Lifecycle;
  return 'needs_correction';
}

function errorMessage(err: unknown, fallback: string): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data
    ?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') return JSON.stringify(detail);
  return (err as { message?: string }).message ?? fallback;
}

function shouldShowRefreshGrns(req: APRequest): boolean {
  return ['GRN_UNAPPROVED', 'GRN_NOT_FOUND', 'MULTIPLE_GRNS'].includes(
    String(req.bucket ?? ''),
  );
}

function isAtLeastAsFresh(candidate: APRequest, baseline: APRequest | null): boolean {
  if (!baseline) return true;
  const candidateTime = Date.parse(candidate.updated_at ?? '');
  const baselineTime = Date.parse(baseline.updated_at ?? '');
  if (Number.isFinite(candidateTime) && Number.isFinite(baselineTime)) {
    return candidateTime >= baselineTime;
  }
  return candidate.updated_at === baseline.updated_at;
}

export interface DetailPanelProps {
  // Accept either the AP-specialised request or the generic page-level
  // ApprovalRequest (the queue page passes the latter). We narrow internally.
  request: ApprovalRequest | null;
  onAction?: () => void;
}

export function DetailPanel({ request: rawRequest, onAction }: DetailPanelProps) {
  const request = rawRequest as APRequest | null;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [serviceOpen, setServiceOpen] = useState(false);

  const formHandleRef = useRef<ReprocessFormHandle | null>(null);
  const [formIsDirty, setFormIsDirty] = useState(false);
  const [seedSupplier, setSeedSupplier] = useState<SupplierCandidate | null>(null);

  const handleSupplierPicked = (s: SupplierHit) => {
    setSeedSupplier({
      supplier_id: s.supplier_id,
      supplier_code: s.supplier_code ?? null,
      company_name: s.company_name,
      ntn_no: s.ntn_no ?? null,
    });
  };

  const detailQuery = useQuery<APRequest>({
    queryKey: ['detail', SLUG, request?.id],
    queryFn: async () => {
      const res = await apiClient.get(`/automations/${SLUG}/${request!.id}`);
      return res.data as APRequest;
    },
    enabled: !!request?.id,
  });

  const current: APRequest | null =
    detailQuery.data && isAtLeastAsFresh(detailQuery.data, request)
      ? detailQuery.data
      : request ?? null;

  useEffect(() => {
    setRejectOpen(false);
    setRejectReason('');
    setServiceOpen(false);
  }, [request?.id]);

  useEffect(() => {
    if (!request?.id || !detailQuery.data) return;
    if (isAtLeastAsFresh(detailQuery.data, request)) return;
    void queryClient.invalidateQueries({ queryKey: ['detail', SLUG, request.id] });
  }, [detailQuery.data, queryClient, request]);

  const invalidateAll = () => {
    void queryClient.invalidateQueries({ queryKey: QK_QUEUE });
    void queryClient.invalidateQueries({ queryKey: QK_HISTORY });
    void queryClient.invalidateQueries({ queryKey: QK_STATS });
    if (request?.id) {
      void queryClient.invalidateQueries({ queryKey: ['detail', SLUG, request.id] });
    }
    onAction?.();
  };

  const reprocessMutation = useMutation({
    mutationFn: async (vars: { edits: APReprocessEdits; updatedAt: string }) => {
      const idem = deriveIdempotencyKey(request!.id, vars.updatedAt, 'reprocess');
      const res = await apiClient.post(
        `/automations/${SLUG}/${request!.id}/reprocess`,
        {
          updated_at: vars.updatedAt,
          edits: vars.edits,
          idempotency_key: idem,
        },
        { headers: { 'Idempotency-Key': idem } },
      );
      return res.data;
    },
    onSuccess: (data: { lifecycle: Lifecycle; request?: APRequest }) => {
      if (data.request) {
        queryClient.setQueryData(['detail', SLUG, data.request.id], data.request);
      }
      toast({
        variant: 'success',
        title: 'Reprocessed',
        description:
          data.lifecycle === 'pending_approval'
            ? 'Row is now ready to approve.'
            : 'Row updated; still needs correction.',
      });
      invalidateAll();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string }; status?: number } }).response;
      const status = detail?.status;
      const msg =
        detail?.data?.detail ??
        (err as { message?: string }).message ??
        'Reprocess failed';
      if (status === 409) {
        toast({
          variant: 'warning',
          title: 'Form was updated by another user — reloading',
          description: msg,
        });
        invalidateAll();
      } else {
        toast({ variant: 'error', title: 'Could not save', description: msg });
      }
    },
  });

  const refreshGrnMutation = useMutation({
    mutationFn: async (vars: { updatedAt: string }) => {
      const res = await apiClient.post(
        `/automations/${SLUG}/${request!.id}/refresh-grn-candidates`,
        { updated_at: vars.updatedAt, auto_post: true },
      );
      return res.data;
    },
    onSuccess: (data: { request?: APRequest }) => {
      if (data.request) {
        queryClient.setQueryData(['detail', SLUG, data.request.id], data.request);
      }
      toast({
        variant: 'success',
        title: 'GRN candidates refreshed',
        description: 'Oracle candidate status was checked.',
      });
      invalidateAll();
    },
    onError: (err: unknown) => {
      toast({
        variant: 'error',
        title: 'Could not refresh GRNs',
        description: errorMessage(err, 'Refresh failed'),
      });
    },
  });

  const approveMutation = useMutation({
    mutationFn: async (vars: {
      edits: APReprocessEdits | null;
      updatedAt: string;
    }) => {
      const idem = deriveIdempotencyKey(request!.id, vars.updatedAt, 'approve');
      const res = await apiClient.post(
        `/automations/${SLUG}/${request!.id}/approve`,
        {
          updated_at: vars.updatedAt,
          edits: vars.edits ?? undefined,
          idempotency_key: idem,
        },
        { headers: { 'Idempotency-Key': idem } },
      );
      return res.data;
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Approved & posted',
        description: 'Oracle accepted the purchase invoice.',
      });
      invalidateAll();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: unknown }; status?: number } }).response;
      const status = detail?.status;
      const raw = detail?.data?.detail;
      const msg =
        typeof raw === 'string'
          ? raw
          : raw && typeof raw === 'object'
            ? JSON.stringify(raw)
            : (err as { message?: string }).message ?? 'Approve failed';
      if (status === 409) {
        toast({
          variant: 'warning',
          title: 'Form was updated by another user — reloading',
          description: msg,
        });
        invalidateAll();
      } else {
        toast({ variant: 'error', title: 'Could not approve', description: msg });
      }
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (vars: { reason: string; updatedAt: string }) => {
      const idem = deriveIdempotencyKey(request!.id, vars.updatedAt, 'reject');
      const res = await apiClient.post(
        `/automations/${SLUG}/${request!.id}/reject`,
        {
          updated_at: vars.updatedAt,
          idempotency_key: idem,
          reason: vars.reason,
        },
        { headers: { 'Idempotency-Key': idem } },
      );
      return res.data;
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Request rejected',
        description: 'The audit row has been written.',
      });
      setRejectOpen(false);
      setRejectReason('');
      invalidateAll();
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } }; message?: string })
          ?.response?.data?.detail ??
        (err as { message?: string }).message ??
        'Reject failed';
      toast({ variant: 'error', title: 'Could not reject', description: detail });
    },
  });

  // Clear the SUPPLIER_NOT_FOUND seed when the selected row changes so the
  // picked supplier doesn't bleed into the next request.
  useEffect(() => {
    setSeedSupplier(null);
  }, [request?.id]);

  // cmd/ctrl-Enter triggers Save & validate when form is dirty.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        if (formIsDirty && !reprocessMutation.isPending && current) {
          e.preventDefault();
          handleSave();
        }
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formIsDirty, reprocessMutation.isPending, current?.id, current?.updated_at]);

  if (!current) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-8 rounded-lg border border-dashed border-neutral-300 bg-neutral-50 p-32 text-center">
        <p className="text-base font-medium text-neutral-700">
          Select a work item from the queue
        </p>
        <p className="text-sm text-neutral-500">
          Pick a row on the left to see details and actions.
        </p>
      </div>
    );
  }

  const payload = (current.payload ?? ({} as APPayload)) as APPayload;
  const summary = (current.summary ?? ({} as APSummary)) as APSummary;
  const lifecycle = deriveLifecycle(current);
  const isTerminal = lifecycle === 'posted' || lifecycle === 'rejected';
  const showApprove = shouldShowApprove(lifecycle);
  const approveBlockedReason = approveDisabledReason(lifecycle, formIsDirty);
  const showReject = !isTerminal;
  const showPostAsService = shouldShowPostAsService(
    payload,
    summary,
    lifecycle,
    current.bucket,
  );
  const servicePostBlockedReason = servicePostDisabledReason(formIsDirty, current.bucket);
  const showRefreshGrns = shouldShowRefreshGrns(current);
  const refreshGrnBlockedReason = formIsDirty ? 'Save changes first' : null;

  function handleSave() {
    if (!current || !formHandleRef.current) return;
    reprocessMutation.mutate({
      edits: formHandleRef.current.edits,
      updatedAt: current.updated_at,
    });
  }

  function handleRefreshGrns() {
    if (!current) return;
    refreshGrnMutation.mutate({ updatedAt: current.updated_at });
  }

  function handleApprove() {
    if (!current) return;
    const edits =
      formIsDirty && formHandleRef.current ? formHandleRef.current.edits : null;
    approveMutation.mutate({ edits, updatedAt: current.updated_at });
  }

  function handleConfirmReject() {
    const reason = rejectReason.trim();
    if (!reason || !current) return;
    rejectMutation.mutate({ reason, updatedAt: current.updated_at });
  }

  return (
    <div className="flex flex-col gap-16">
      <BucketView req={current} onSupplierPicked={handleSupplierPicked} />

      <APActivityComments
        requestId={current.id}
        actions={current.actions ?? []}
        onAction={invalidateAll}
      />

      {!isTerminal && (
        <section className="rounded-lg border border-neutral-200 bg-neutral-0 p-16">
          <ReprocessForm
            request={current}
            disabled={isTerminal}
            seedSupplier={seedSupplier}
            onChange={(handle) => {
              formHandleRef.current = handle;
              setFormIsDirty(handle.isDirty);
            }}
          />
        </section>
      )}

      {rejectOpen && !isTerminal && (
        <div className="rounded-lg border border-error-500/40 bg-error-50 p-16">
          <p className="mb-8 text-sm font-medium text-error-700">Reject reason</p>
          <textarea
            className="block min-h-[160px] w-full resize-y rounded-md border border-neutral-200 bg-neutral-0 px-12 py-8 text-sm text-neutral-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Why is this row being rejected? This is appended to the audit trail."
          />
          <div className="mt-12 flex flex-wrap justify-end gap-8">
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={() => {
                setRejectOpen(false);
                setRejectReason('');
              }}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="destructive"
              size="sm"
              onClick={handleConfirmReject}
              disabled={!rejectReason.trim() || rejectMutation.isPending}
              loading={rejectMutation.isPending}
            >
              Confirm reject
            </ActionButton>
          </div>
        </div>
      )}

      {!isTerminal && (
        <footer className="flex flex-wrap items-center gap-8 rounded-lg border border-neutral-200 bg-neutral-0 p-12">
          <DrivePreview payload={payload} actions={current.actions ?? []} />
          {showReject && (
            <ActionButton
              variant="destructive"
              onClick={() => setRejectOpen(true)}
              disabled={rejectMutation.isPending}
            >
              Reject
            </ActionButton>
          )}
          <div className="ml-auto flex flex-wrap items-center gap-8">
            {showRefreshGrns && (
              <span
                title={
                  refreshGrnBlockedReason ??
                  'Re-checks WizERP for newly-approved GRN matches. Finance must approve in WizERP first.'
                }
              >
                <ActionButton
                  variant="secondary"
                  onClick={handleRefreshGrns}
                  disabled={
                    refreshGrnMutation.isPending || refreshGrnBlockedReason !== null
                  }
                  loading={refreshGrnMutation.isPending}
                >
                  Refresh GRN candidates
                </ActionButton>
              </span>
            )}
            {showPostAsService && (
              <span title={servicePostBlockedReason ?? ''}>
                <ActionButton
                  variant="secondary"
                  onClick={() => setServiceOpen(true)}
                  disabled={servicePostBlockedReason !== null}
                >
                  Prepare service invoice
                </ActionButton>
              </span>
            )}
            <ActionButton
              variant="secondary"
              onClick={handleSave}
              disabled={!formIsDirty || reprocessMutation.isPending}
              loading={reprocessMutation.isPending}
            >
              Save & validate
            </ActionButton>
            {showApprove && (
              <span title={approveBlockedReason ?? ''}>
                <ActionButton
                  onClick={handleApprove}
                  loading={approveMutation.isPending}
                  disabled={approveMutation.isPending || approveBlockedReason !== null}
                >
                  Approve & post
                </ActionButton>
              </span>
            )}
          </div>
        </footer>
      )}

      {isTerminal && (
        <footer className="flex items-center justify-end rounded-lg border border-neutral-200 bg-neutral-0 p-12">
          <DrivePreview payload={payload} actions={current.actions ?? []} />
        </footer>
      )}

      <ServiceInvoiceModal
        requestId={current.id}
        payload={payload}
        updatedAt={current.updated_at}
        open={serviceOpen}
        onClose={() => setServiceOpen(false)}
        onSuccess={() => invalidateAll()}
      />

      {/* Intentionally avoid dropping `summary` so TS knows we use it. */}
      <span className="sr-only">{summary.type ?? ''}</span>
    </div>
  );
}

// Maintain backward-compatible export name for any importer still using
// `APInvoiceDetailPanel` (e.g. the AutomationConfig in `index.ts`).
export const APInvoiceDetailPanel = DetailPanel;
